import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Coins, Warning, Check, CaretRight } from '@phosphor-icons/react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    overdue:  { chip: 'bg-red-500/15 border-red-400/40 text-red-300', label: 'Achterstallig', Icon: Warning },
    due_now:  { chip: 'bg-[#D4A537]/15 border-[#D4A537]/40 text-[#D4A537]', label: 'Nu innen', Icon: Coins },
    ok:       { chip: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300', label: 'Op schema', Icon: Check },
};

function formatEuro(n) {
    if (n == null) return '';
    return `€${Number(n).toFixed(0)}`;
}

/**
 * Full administratie overview — every student with a payment plan grouped by
 * urgency (overdue → due-now → ok). Click a row to jump to the student card.
 */
export default function AdministratieModal({ open, onClose, onSelectStudent }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [markingPaid, setMarkingPaid] = useState(null);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/administratie/overview`, { withCredentials: true });
                setRows(data || []);
            } catch {
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    const markInstallmentPaid = async (row) => {
        if (markingPaid || row.next_installment == null) return;
        setMarkingPaid(row.id);
        try {
            const newPaid = [...(row.paid_installments || []), row.next_installment].sort((a, b) => a - b);
            await axios.put(
                `${API}/students/${row.id}`,
                { paid_installments: newPaid },
                { withCredentials: true }
            );
            toast.success('Termijn afgevinkt ✅');
            // Refresh
            const { data } = await axios.get(`${API}/administratie/overview`, { withCredentials: true });
            setRows(data || []);
        } catch (e) {
            toast.error('Kon niet opslaan');
        } finally {
            setMarkingPaid(null);
        }
    };

    const dueCount = rows.filter(r => r.status !== 'ok').length;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent
                className="bg-[#1A202C] border-2 border-[#D4A537] text-white max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh]"
                data-testid="administratie-modal"
            >
                <div className="px-5 py-4 bg-gradient-to-br from-[#D4A537]/20 via-[#D4A537]/5 to-transparent border-b border-[#4A5568]">
                    <div className="flex items-center gap-2">
                        <Coins size={24} weight="fill" className="text-[#D4A537]" />
                        <DialogTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Administratie
                        </DialogTitle>
                        {dueCount > 0 && (
                            <span className="ml-auto text-[11px] font-bold rounded-full px-2 py-0.5 bg-red-500 text-white">
                                {dueCount} openstaand
                            </span>
                        )}
                    </div>
                    <DialogDescription className="text-xs text-gray-300 mt-1">
                        Wie moet de volgende termijn betalen?
                    </DialogDescription>
                </div>

                <div className="px-4 py-3 space-y-2 overflow-y-auto max-h-[70vh]">
                    {loading && (
                        <div className="text-center text-sm text-gray-400 py-6">Laden…</div>
                    )}
                    {!loading && rows.length === 0 && (
                        <div className="text-center py-8">
                            <div className="w-14 h-14 mx-auto rounded-full bg-[#2D3748] flex items-center justify-center mb-2">
                                <Check size={26} className="text-emerald-400" weight="fill" />
                            </div>
                            <p className="text-sm text-white font-semibold">Geen openstaande betalingen</p>
                            <p className="text-[11px] text-gray-500 mt-1">
                                Zodra een leerling 1 les vóór de volgende termijn zit, komt hij hier automatisch te staan.
                            </p>
                        </div>
                    )}
                    {rows.map(r => {
                        const style = STATUS_STYLES[r.status] || STATUS_STYLES.ok;
                        const StyleIcon = style.Icon;
                        return (
                            <div
                                key={r.id}
                                className={`bg-[#2D3748] border rounded-xl p-3 transition-colors hover:border-[#D4A537]/40 ${
                                    r.status === 'overdue' ? 'border-red-500/60' :
                                    r.status === 'due_now' ? 'border-[#D4A537]/60' : 'border-[#4A5568]'
                                }`}
                                data-testid={`administratie-row-${r.id}`}
                            >
                                <div
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => { onClose(); onSelectStudent?.(r.id); }}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                            {r.name}
                                        </div>
                                        <div className="text-[11px] text-gray-400 truncate">
                                            {r.package} · Les {Math.floor(r.lessons_used)} / {r.total_lessons}
                                        </div>
                                    </div>
                                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${style.chip} flex-shrink-0`}>
                                        <StyleIcon size={10} weight="fill" />
                                        {style.label}
                                    </span>
                                    <CaretRight size={14} weight="bold" className="text-gray-500 flex-shrink-0" />
                                </div>

                                {r.next_installment != null && (
                                    <div className="mt-2 pt-2 border-t border-[#4A5568]/50 flex items-center justify-between gap-2">
                                        <div className="text-[11px] text-gray-300">
                                            Termijn <span className="text-white font-semibold">{r.next_installment + 1}/{r.installments}</span>
                                            &nbsp;·&nbsp;<span className="text-[#D4A537] font-semibold">{formatEuro(r.amount_per_installment)}</span>
                                            {r.next_installment_lesson != null && (
                                                <span className="text-gray-500"> · vóór les {r.next_installment_lesson}</span>
                                            )}
                                        </div>
                                        {r.status !== 'ok' && (
                                            <Button
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); markInstallmentPaid(r); }}
                                                disabled={markingPaid === r.id}
                                                className="h-6 px-2 bg-emerald-500 hover:bg-emerald-400 text-white text-[10px] font-semibold"
                                                data-testid={`administratie-mark-paid-${r.id}`}
                                            >
                                                {markingPaid === r.id ? 'Bezig' : 'Afvinken ✓'}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
